import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createPoint as apiCreatePoint,
  createProject as apiCreateProject,
  createProjectLog,
  createTeam as apiCreateTeam,
  deletePafta as apiDeletePafta,
  deleteProject as apiDeleteProject,
  deleteTeam as apiDeleteTeam,
  fetchProjects,
  fetchWorkspace,
  renameProject as apiRenameProject,
  saveMeasurementTemplate,
  updatePafta as apiUpdatePafta,
  updatePoint as apiUpdatePoint,
  updateTeam as apiUpdateTeam,
} from "../api";
import {
  getApiErrorMessage,
  getConflictCurrent,
  isConflictError,
} from "../api/client";
import {
  LEGACY_PROJECTS_LIST_KEY,
  getLegacyProjectDataKey,
  getLegacySyncQueueKey,
  getLegacyTemplateKey,
  getProjectDataKey,
  getProjectsListKey,
  getSyncQueueKey,
  getTemplateKey,
} from "../constants/storageKeys";
import {
  mutateQueueByKey,
  readOfflineState,
  removeOfflineState,
  writeOfflineState,
} from "../offline/offlineStore";
import { applyQueueToWorkspace } from "../offline/offlineWorkspaceModel";
import { applyPointServerFields } from "../utils/pointState";
import { createClientEntityId } from "../utils/clientIds";
import {
  DEFAULT_TEMPLATE,
  generateTemplateRowId,
  isAuthzError,
  normalizeTemplateRows,
} from "./projectDataUtils";

function workspaceNeedsPhotoRefresh(workspace, cachedPoints) {
  if (workspace?.photosIncluded !== false) return false;
  const cachedById = new Map(
    (cachedPoints || []).map((point) => [String(point.id), point])
  );

  return (workspace.points || []).some((point) => {
    const cachedPoint = cachedById.get(String(point.id));
    if (!cachedPoint) return Boolean(point.hasPhoto);
    return String(cachedPoint.photoSignature || "") !== String(point.photoSignature || "");
  });
}

export function useProjectData({
  isOnline,
  selectedProjectId,
  setSelectedProjectId,
  addToSyncQueue,
  loadQueueByKey,
  syncPulse,
  ownerUserId,
}) {
  const [projects, setProjects] = useState([]);
  const [projectsOwnerUserId, setProjectsOwnerUserId] = useState(null);

  const [paftalar, setPaftalar] = useState([]);
  const [points, setPoints] = useState([]);
  const [teams, setTeams] = useState([]);
  const [measurementTemplate, setMeasurementTemplate] = useState([]);
  const [hydratedProjectId, setHydratedProjectId] = useState(null);
  const [hydratedOwnerUserId, setHydratedOwnerUserId] = useState(null);
  const [workspaceRefreshTick, setWorkspaceRefreshTick] = useState(0);
  const templateSaveVersionRef = useRef(0);
  const pointsRef = useRef([]);

  const wasOnlineRef = useRef(isOnline);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  const projectDataKey = useCallback(
    (projectId) => getProjectDataKey(ownerUserId, projectId),
    [ownerUserId]
  );
  const templateKey = useCallback(
    (projectId) => getTemplateKey(ownerUserId, projectId),
    [ownerUserId]
  );

  const sortedTemplate = useMemo(
    () =>
      [...measurementTemplate].map((row, index) => ({
        ...row,
        order: index + 1,
      })),
    [measurementTemplate]
  );

  const persistProjects = useCallback(
    async (nextProjects) => {
      if (!ownerUserId) return;
      await writeOfflineState(getProjectsListKey(ownerUserId), nextProjects);
    },
    [ownerUserId]
  );

  useEffect(() => {
    if (!ownerUserId) {
      setProjects([]);
      setProjectsOwnerUserId(null);
      return undefined;
    }

    let cancelled = false;
    setProjects([]);
    setProjectsOwnerUserId(String(ownerUserId));
    readOfflineState(getProjectsListKey(ownerUserId), LEGACY_PROJECTS_LIST_KEY)
      .then((cachedProjects) => {
        if (cancelled || !Array.isArray(cachedProjects)) return;
        setProjects((previous) => {
          const serverIds = new Set(previous.map((item) => String(item.id)));
          return [
            ...previous,
            ...cachedProjects.filter((item) => !serverIds.has(String(item.id))),
          ];
        });
      })
      .catch((error) => {
        console.error("Proje listesi çevrimdışı depodan okunamadı:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [ownerUserId]);

  const currentProject = useMemo(
    () =>
      projects.find(
        (project) => String(project.id) === String(selectedProjectId)
      ) || null,
    [projects, selectedProjectId]
  );

  const upsertProjectInList = useCallback(
    (projectRow) => {
      if (!projectRow?.id) return;

      setProjectsOwnerUserId(String(ownerUserId || ""));
      setProjects((previous) => {
        const existingIndex = previous.findIndex((item) => item.id === projectRow.id);
        const nextProjects =
          existingIndex === -1
            ? [...previous, projectRow]
            : previous.map((item) => (item.id === projectRow.id ? { ...item, ...projectRow } : item));

        persistProjects(nextProjects).catch((error) => {
          console.error("Proje listesi çevrimdışı depoya yazılamadı:", error);
        });
        return nextProjects;
      });
    },
    [ownerUserId, persistProjects]
  );

  const applyWorkspaceToState = useCallback(
    (projectId, workspace) => {
      setPaftalar(workspace.paftalar || []);
      const serverPoints = workspace.points || [];
      const nextPoints = workspace.photosIncluded === false
        ? serverPoints.map((point) => {
            const cachedPoint = pointsRef.current.find(
              (item) => String(item.id) === String(point.id)
            );
            return cachedPoint
              ? {
                  ...point,
                  photoDataUrl: cachedPoint.photoDataUrl || "",
                  photoDataUrls: cachedPoint.photoDataUrls || [],
                }
              : point;
          })
        : serverPoints;
      pointsRef.current = nextPoints;
      setPoints(nextPoints);
      setTeams(workspace.teams || []);
      const nextTemplate = normalizeTemplateRows(
        workspace.measurementTemplate,
        DEFAULT_TEMPLATE
      );
      setMeasurementTemplate(nextTemplate);
      setHydratedProjectId(projectId);
      setHydratedOwnerUserId(String(ownerUserId));

      if (workspace.project) {
        upsertProjectInList(workspace.project);
      }

      saveProjectDataToLocal(
        projectId,
        workspace.paftalar || [],
        nextPoints,
        workspace.teams || []
      ).catch((error) => {
        console.error("Proje verisi çevrimdışı depoya yazılamadı:", error);
      });
      saveProjectTemplateToLocal(projectId, nextTemplate).catch((error) => {
        console.error("Ölçüm şablonu çevrimdışı depoya yazılamadı:", error);
      });
    },
    [ownerUserId, upsertProjectInList]
  );

  const reloadWorkspaceFromServer = useCallback(
    async (projectId = selectedProjectId) => {
      if (!projectId || !isOnline) return false;

      const queue = await loadQueueByKey(
        getSyncQueueKey(ownerUserId, projectId),
        getLegacySyncQueueKey(projectId)
      );
      if (Array.isArray(queue) && queue.length > 0) {
        return false;
      }

      try {
        let workspace = await fetchWorkspace(projectId, { includePhotos: false });
        if (workspaceNeedsPhotoRefresh(workspace, pointsRef.current)) {
          workspace = await fetchWorkspace(projectId, { includePhotos: true });
        }
        applyWorkspaceToState(projectId, workspace);
        return true;
      } catch (error) {
        console.error("Çalışma alanı yenilenemedi:", error);
        return false;
      }
    },
    [applyWorkspaceToState, isOnline, loadQueueByKey, ownerUserId, selectedProjectId]
  );

  const saveProjectDataToLocal = useCallback(
    async (projectId, nextPaftalar, nextPoints, nextTeams) => {
      if (!projectId) return;

      await writeOfflineState(
        projectDataKey(projectId),
        {
          paftaList: nextPaftalar,
          pointList: nextPoints,
          teamList: nextTeams,
          timestamp: Date.now(),
        }
      );
    },
    [projectDataKey]
  );

  const saveProjectTemplateToLocal = useCallback(
    async (projectId, nextTemplate) => {
      if (!projectId) return;
      await writeOfflineState(templateKey(projectId), nextTemplate);
    },
    [templateKey]
  );

  const persistTemplate = useCallback(
    async (nextTemplate) => {
      if (!selectedProjectId) return;

      const normalized = normalizeTemplateRows(nextTemplate, DEFAULT_TEMPLATE);
      const requestVersion = ++templateSaveVersionRef.current;

      setMeasurementTemplate(normalized);
      await saveProjectTemplateToLocal(selectedProjectId, normalized);

      const task = {
        type: "updateProjectTemplate",
        projectId: selectedProjectId,
        baseUpdatedAt: currentProject?.updatedAt ?? null,
        data: {
          measurementTemplate: normalized,
        },
      };

      if (!isOnline) {
        await addToSyncQueue(task, selectedProjectId);
        return;
      }

      try {
        const response = await saveMeasurementTemplate(
          selectedProjectId,
          normalized,
          currentProject?.updatedAt ?? null
        );
        if (response?.project) {
          upsertProjectInList(response.project);
        }
        if (Array.isArray(response?.measurementTemplate)) {
          if (requestVersion !== templateSaveVersionRef.current) {
            return;
          }
          const serverTemplate = normalizeTemplateRows(
            response.measurementTemplate,
            normalized
          );
          setMeasurementTemplate(serverTemplate);
          await saveProjectTemplateToLocal(selectedProjectId, serverTemplate);
        }
      } catch (error) {
        if (isConflictError(error)) {
          if (requestVersion !== templateSaveVersionRef.current) {
            return;
          }
          const current = getConflictCurrent(error);
          if (current) {
            upsertProjectInList(current);
            const nextServerTemplate = normalizeTemplateRows(
              current.measurementTemplate,
              DEFAULT_TEMPLATE
            );
            setMeasurementTemplate(nextServerTemplate);
            await saveProjectTemplateToLocal(selectedProjectId, nextServerTemplate);
          }
          alert(getApiErrorMessage(error, "Ölçüm şablonu başka bir oturumda değişti."));
          return;
        }

        console.error("Şablon kaydedilemedi:", error);
        await addToSyncQueue(task, selectedProjectId);
      }
    },
    [
      addToSyncQueue,
      currentProject?.updatedAt,
      isOnline,
      saveProjectTemplateToLocal,
      selectedProjectId,
      upsertProjectInList,
    ]
  );

  const replacePointRow = useCallback((pointRow) => {
    if (!pointRow?.id) return;
    setPoints((previous) =>
      previous.map((point) => (point.id === pointRow.id ? { ...point, ...pointRow } : point))
    );
  }, []);

  const applyPointResponseFields = useCallback((pointId, pointRow, fields = []) => {
    if (!pointId || !pointRow) return;
    setPoints((previous) => applyPointServerFields(previous, pointId, pointRow, fields));
  }, []);

  const replacePaftaRow = useCallback((paftaRow) => {
    if (!paftaRow?.id) return;
    setPaftalar((previous) =>
      previous.map((pafta) => (pafta.id === paftaRow.id ? { ...pafta, ...paftaRow } : pafta))
    );
  }, []);

  const replaceTeamRow = useCallback((teamRow) => {
    if (!teamRow?.id) return;
    setTeams((previous) =>
      previous.map((team) => (team.id === teamRow.id ? { ...team, ...teamRow } : team))
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function syncProjectsFromServer() {
      if (!isOnline || !ownerUserId) return;

      try {
        const serverProjects = await fetchProjects();
        if (cancelled) return;

        setProjects((previous) => {
          const localOnly = previous.filter(
            (project) =>
              !serverProjects.some(
                (item) => String(item.id) === String(project.id)
              )
          );
          const merged = [...serverProjects, ...localOnly];
          persistProjects(merged).catch((error) => {
            console.error("Proje listesi çevrimdışı depoya yazılamadı:", error);
          });

          if (!selectedProjectId && merged.length > 0) {
            setSelectedProjectId(String(merged[0].id));
          } else if (
            selectedProjectId &&
            !merged.some(
              (project) => String(project.id) === String(selectedProjectId)
            ) &&
            merged.length > 0
          ) {
            setSelectedProjectId(String(merged[0].id));
          }

          return merged;
        });
        setProjectsOwnerUserId(String(ownerUserId));
      } catch (error) {
        console.error("Projeler sunucudan alınamadı:", error);
      }
    }

    syncProjectsFromServer();

    return () => {
      cancelled = true;
    };
  }, [isOnline, ownerUserId, persistProjects, selectedProjectId, setSelectedProjectId, syncPulse]);

  useEffect(() => {
    if (!isOnline || !selectedProjectId) return undefined;

    const timer = setInterval(() => {
      setWorkspaceRefreshTick((value) => value + 1);
    }, 60000);
    const refreshOnFocus = () => {
      setWorkspaceRefreshTick((value) => value + 1);
    };
    const refreshOnVisibility = () => {
      if (document.visibilityState === "visible") {
        setWorkspaceRefreshTick((value) => value + 1);
      }
    };

    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnVisibility);

    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnVisibility);
    };
  }, [isOnline, selectedProjectId]);

  useEffect(() => {
    let cancelled = false;

    async function hydrateProject() {
      const projectId = selectedProjectId;
      if (!projectId || !ownerUserId) return;
      const isSameHydratedProject =
        hydratedProjectId != null &&
        String(hydratedProjectId) === String(projectId) &&
        String(hydratedOwnerUserId || "") === String(ownerUserId);
      const queue = await loadQueueByKey(
        getSyncQueueKey(ownerUserId, projectId),
        getLegacySyncQueueKey(projectId)
      );
      let hadCachedWorkspace = isSameHydratedProject;

      if (!isSameHydratedProject) {
        setPaftalar([]);
        setPoints([]);
        pointsRef.current = [];
        setTeams([]);
        setMeasurementTemplate([]);

        try {
          const cachedWorkspace = await readOfflineState(
            projectDataKey(projectId),
            getLegacyProjectDataKey(projectId)
          );
          hadCachedWorkspace = Boolean(cachedWorkspace);
          const localWorkspace = applyQueueToWorkspace(cachedWorkspace, queue);
          if (!cancelled) {
            setPaftalar(localWorkspace.paftaList);
            setPoints(localWorkspace.pointList);
            pointsRef.current = localWorkspace.pointList;
            setTeams(localWorkspace.teamList);
            setHydratedProjectId(projectId);
            setHydratedOwnerUserId(String(ownerUserId));
          }
        } catch (error) {
          console.error("Yerel proje verisi okunamadı:", error);
          if (!cancelled) {
            setHydratedProjectId(projectId);
            setHydratedOwnerUserId(String(ownerUserId));
          }
        }

        try {
          const cachedTemplate = await readOfflineState(
            templateKey(projectId),
            getLegacyTemplateKey(projectId)
          );
          const queuedTemplate = [...queue]
            .reverse()
            .find((task) => task.type === "updateProjectTemplate")
            ?.data?.measurementTemplate;
          if (!cancelled) {
            setMeasurementTemplate(
              normalizeTemplateRows(
                queuedTemplate || cachedTemplate || DEFAULT_TEMPLATE,
                DEFAULT_TEMPLATE
              )
            );
          }
        } catch (error) {
          console.error("Yerel şablon okunamadı:", error);
          if (!cancelled) {
            setMeasurementTemplate(normalizeTemplateRows(DEFAULT_TEMPLATE, DEFAULT_TEMPLATE));
          }
        }
      }

      if (!isOnline) return;

      if (Array.isArray(queue) && queue.length > 0) {
        return;
      }

      try {
        let workspace = await fetchWorkspace(projectId, {
          includePhotos: !hadCachedWorkspace,
        });
        if (workspaceNeedsPhotoRefresh(workspace, pointsRef.current)) {
          workspace = await fetchWorkspace(projectId, { includePhotos: true });
        }
        if (cancelled) return;

        applyWorkspaceToState(projectId, workspace);
      } catch (error) {
        console.error("Proje çalışma alanı alınamadı:", error);
      }
    }

    if (
      wasOnlineRef.current &&
      !isOnline &&
      String(hydratedProjectId) === String(selectedProjectId)
      && String(hydratedOwnerUserId || "") === String(ownerUserId || "")
    ) {
      wasOnlineRef.current = false;
      return undefined;
    }

    wasOnlineRef.current = isOnline;
    hydrateProject();

    return () => {
      cancelled = true;
    };
  }, [
    applyWorkspaceToState,
    hydratedProjectId,
    hydratedOwnerUserId,
    isOnline,
    loadQueueByKey,
    ownerUserId,
    projectDataKey,
    selectedProjectId,
    syncPulse,
    templateKey,
    workspaceRefreshTick,
  ]);

  useEffect(() => {
    if (
      !selectedProjectId ||
      !ownerUserId ||
      String(hydratedOwnerUserId || "") !== String(ownerUserId) ||
      String(hydratedProjectId) !== String(selectedProjectId)
    ) {
      return;
    }
    saveProjectDataToLocal(selectedProjectId, paftalar, points, teams).catch((error) => {
      console.error("Proje verisi çevrimdışı depoya yazılamadı:", error);
    });
  }, [
    hydratedProjectId,
    hydratedOwnerUserId,
    ownerUserId,
    paftalar,
    points,
    saveProjectDataToLocal,
    selectedProjectId,
    teams,
  ]);

  const paftaGorunurlukDegistir = (paftaId) => {
    setPaftalar((previous) =>
      previous.map((pafta) =>
        pafta.id === paftaId ? { ...pafta, gorunur: !pafta.gorunur } : pafta
      )
    );
  };

  const addNewTeam = async (name) => {
    const trimmed = String(name || "").trim();
    const visiblePaftalar = paftalar.filter((pafta) => pafta.gorunur);

    if (visiblePaftalar.length !== 1) {
      alert("Takım oluşturmak için tek bir pafta görünür olmalı.");
      return;
    }

    if (!trimmed) return;

    const activePafta = visiblePaftalar[0];
    const team = {
      id: createClientEntityId("team"),
      name: trimmed,
      members: [],
      paftaId: activePafta.id,
      projectId: selectedProjectId,
    };

    setTeams((previous) => [...previous, team]);

    const logTask = {
      taskId: createClientEntityId("log"),
      type: "createLog",
      projectId: selectedProjectId,
      data: {
        type: "team_created",
        message: `Takım oluşturuldu: ${trimmed}`,
      },
    };

    const teamTask = {
      type: "createTeam",
      projectId: selectedProjectId,
      data: team,
    };

    if (!isOnline) {
      await addToSyncQueue(teamTask, selectedProjectId);
      await addToSyncQueue(logTask, selectedProjectId);
      return;
    }

    try {
      const createdTeam = await apiCreateTeam(selectedProjectId, team);
      setTeams((previous) =>
        previous.map((item) => (item.id === team.id ? createdTeam : item))
      );
      await createProjectLog(selectedProjectId, {
        ...logTask.data,
        clientRequestId: logTask.taskId,
      });
    } catch (error) {
      if (isAuthzError(error)) {
        await reloadWorkspaceFromServer();
        alert(getApiErrorMessage(error, "Takım oluşturma yetkiniz bulunmuyor."));
        return;
      }
      console.error("Takım oluşturulamadı:", error);
      await addToSyncQueue(teamTask, selectedProjectId);
      await addToSyncQueue(logTask, selectedProjectId);
    }
  };

  const updateTeamMembersLocal = (teamId, members) => {
    setTeams((previous) =>
      previous.map((team) => (team.id === teamId ? { ...team, members } : team))
    );
  };

  const syncProjectsFromServerNow = async () => {
    if (!isOnline) return false;

    try {
      const serverProjects = await fetchProjects();
      setProjects(serverProjects);
      await persistProjects(serverProjects);

      if (
        !serverProjects.some(
          (project) => String(project.id) === String(selectedProjectId)
        )
      ) {
        setSelectedProjectId(serverProjects[0]?.id ? String(serverProjects[0].id) : null);
      }

      return true;
    } catch (error) {
      console.error("Projeler yenilenemedi:", error);
      return false;
    }
  };

  const createEmptyProjectSafe = async (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;

    const newProject = {
      id: createClientEntityId("project"),
      name: trimmed,
      createdAt: Date.now(),
      updatedAt: null,
    };

    const nextProjects = [...projects, newProject];
    setProjects(nextProjects);
    await persistProjects(nextProjects);
    setSelectedProjectId(String(newProject.id));

    const task = {
      type: "createProject",
      projectId: newProject.id,
      data: { name: trimmed },
    };

    if (!isOnline) {
      await addToSyncQueue(task, newProject.id);
      return;
    }

    try {
      const createdProject = await apiCreateProject({ id: newProject.id, name: trimmed });
      upsertProjectInList(createdProject);
    } catch (error) {
      console.error("Proje oluşturulamadı:", error);
      await addToSyncQueue(task, newProject.id);
    }
  };

  const renameProjectSafe = async (projectId, newName) => {
    const trimmed = String(newName || "").trim();
    if (!projectId || !trimmed) return;

    const project = projects.find((item) => String(item.id) === String(projectId));
    const baseUpdatedAt = project?.updatedAt ?? null;
    const nextProjects = projects.map((projectItem) =>
      String(projectItem.id) === String(projectId)
        ? { ...projectItem, name: trimmed }
        : projectItem
    );

    setProjects(nextProjects);
    await persistProjects(nextProjects);

    const task = {
      type: "renameProject",
      projectId,
      baseUpdatedAt,
      data: { name: trimmed },
    };

    if (!isOnline) {
      await addToSyncQueue(task, projectId);
      return;
    }

    try {
      const updatedProject = await apiRenameProject(projectId, trimmed, baseUpdatedAt);
      upsertProjectInList(updatedProject);
    } catch (error) {
      if (isConflictError(error)) {
        const current = getConflictCurrent(error);
        if (current) {
          upsertProjectInList(current);
        } else {
          await syncProjectsFromServerNow();
        }
        alert(getApiErrorMessage(error, "Proje başka bir oturumda güncellendi."));
        return;
      }

      console.error("Proje adı güncellenemedi:", error);
      await addToSyncQueue(task, projectId);
    }
  };

  const normalizeDeleteConfirmationText = (value) => {
    const turkishCharMap = {
      ç: "c",
      ğ: "g",
      ı: "i",
      ö: "o",
      ş: "s",
      ü: "u",
    };

    return String(value ?? "")
      .trim()
      .toLocaleLowerCase("tr-TR")
      .replace(/\s+/g, " ")
      .replace(/[çğıöşü]/g, (letter) => turkishCharMap[letter] || letter);
  };

  const deleteProjectSafe = async (projectId) => {
    if (!projectId) return;
    if (projects.length <= 1) {
      alert("Son projeyi silemezsiniz.");
      return;
    }

    const project = projects.find((item) => String(item.id) === String(projectId));
    const projectName = String(project?.name || projectId);
    const confirmed = window.confirm(
      `"${projectName}" projesini kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
    );
    if (!confirmed) return;

    const typedPhrase = window.prompt(
      'Silme işlemini onaylamak için "projesi sil" yazın:',
      ""
    );
    if (typedPhrase == null) return;

    const normalizedTypedPhrase = normalizeDeleteConfirmationText(typedPhrase);
    const isValidProjectDeletePhrase =
      normalizedTypedPhrase === normalizeDeleteConfirmationText("projesi sil") ||
      normalizedTypedPhrase === normalizeDeleteConfirmationText("projeyi sil");

    if (!isValidProjectDeletePhrase) {
      alert('Doğrulama metni hatalı. Silme işlemi iptal edildi.');
      return;
    }

    const baseUpdatedAt = project?.updatedAt ?? null;
    const nextProjects = projects.filter(
      (item) => String(item.id) !== String(projectId)
    );
    const removeLocalProject = async () => {
      setProjects(nextProjects);
      await Promise.all([
        persistProjects(nextProjects),
        removeOfflineState(projectDataKey(projectId)),
        removeOfflineState(templateKey(projectId)),
        mutateQueueByKey(
          getSyncQueueKey(ownerUserId, projectId),
          () => [],
          getLegacySyncQueueKey(projectId)
        ),
      ]);

      if (String(selectedProjectId) === String(projectId)) {
        setSelectedProjectId(nextProjects[0]?.id ? String(nextProjects[0].id) : null);
      }
    };

    const task = {
      type: "deleteProject",
      projectId,
      baseUpdatedAt,
    };

    if (!isOnline) {
      await removeLocalProject();
      await addToSyncQueue(task, projectId);
      return;
    }

    try {
      await apiDeleteProject(projectId, baseUpdatedAt);
      await removeLocalProject();
    } catch (error) {
      if (isConflictError(error)) {
        await syncProjectsFromServerNow();
        alert(getApiErrorMessage(error, "Proje başka bir oturumda güncellendi."));
        return;
      }

      console.error("Proje silinemedi:", error);
      await removeLocalProject();
      await addToSyncQueue(task, projectId);
    }
  };

  const changePointStatusSafe = async (pointId, paftaId, newStatus) => {
    const point = points.find((item) => item.id === pointId);
    const baseUpdatedAt = point?.updatedAt ?? null;
    const now = Date.now();
    const updates = { status: newStatus, statusUpdatedAt: now };

    setPoints((previous) =>
      previous.map((item) => (item.id === pointId ? { ...item, ...updates } : item))
    );

    const task = {
      type: "updatePoint",
      projectId: selectedProjectId,
      pointId,
      baseUpdatedAt,
      updates,
    };

    if (!isOnline) {
      await addToSyncQueue(task, selectedProjectId);
      return;
    }

    try {
      const updatedPoint = await apiUpdatePoint(pointId, {
        ...updates,
        baseUpdatedAt,
      });
      applyPointResponseFields(pointId, updatedPoint, ["status", "statusUpdatedAt"]);
    } catch (error) {
      if (isConflictError(error)) {
        const current = getConflictCurrent(error);
        if (current) {
          replacePointRow(current);
        } else {
          await reloadWorkspaceFromServer();
        }
        alert(getApiErrorMessage(error, "Nokta başka bir oturumda güncellendi."));
        return;
      }

      console.error("Nokta durumu güncellenemedi:", error);
      await addToSyncQueue(task, selectedProjectId);
    }
  };

  const createPointLocalFirstSafe = async ({ paftaId, lat, lng, name, ekipId = null }) => {
    if (!paftaId || lat == null || lng == null || !selectedProjectId) return;

    const pafta = paftalar.find((item) => item.id === paftaId);
    const localId = createClientEntityId("point");
    const newPoint = {
      id: localId,
      projectId: selectedProjectId,
      paftaId,
      pafta: pafta?.isim || "",
      ekipId,
      name: name || `nokta-${points.length + 1}`,
      lat,
      lng,
      status: "pending",
      il: "",
      ilce: "",
      koy: "",
      adresTamamlandi: false,
      sicaklik: null,
      atm: null,
      spc: null,
      tds: null,
      sal: null,
      ph: null,
      notMetni: "",
      customMeasurements: {},
      photoDataUrl: "",
      photoDataUrls: [],
      statusUpdatedAt: Date.now(),
      veriGirisiTimestamp: null,
      updatedAt: null,
    };

    setPoints((previous) => [...previous, newPoint]);

    const task = {
      type: "createPoint",
      projectId: selectedProjectId,
      data: newPoint,
    };

    if (!isOnline) {
      await addToSyncQueue(task, selectedProjectId);
      return;
    }

    try {
      const createdPoint = await apiCreatePoint(selectedProjectId, newPoint);
      applyPointResponseFields(localId, createdPoint);
    } catch (error) {
      console.error("Nokta oluşturulamadı:", error);
      await addToSyncQueue(task, selectedProjectId);
    }
  };

  const setPaftaActiveState = async (paftaId, aktif) => {
    const pafta = paftalar.find((item) => item.id === paftaId);
    const baseUpdatedAt = pafta?.updatedAt ?? null;
    const updates = { aktif };

    setPaftalar((previous) =>
      previous.map((item) => (item.id === paftaId ? { ...item, ...updates } : item))
    );

    const task = {
      type: "updatePafta",
      projectId: selectedProjectId,
      paftaId,
      baseUpdatedAt,
      updates,
    };

    if (!isOnline) {
      await addToSyncQueue(task, selectedProjectId);
      return;
    }

    try {
      const updatedPafta = await apiUpdatePafta(paftaId, {
        ...updates,
        baseUpdatedAt,
      });
      replacePaftaRow(updatedPafta);
    } catch (error) {
      if (isConflictError(error)) {
        const current = getConflictCurrent(error);
        if (current) {
          replacePaftaRow(current);
        } else {
          await reloadWorkspaceFromServer();
        }
        alert(getApiErrorMessage(error, "Pafta başka bir oturumda güncellendi."));
        return;
      }

      console.error("Pafta güncellenemedi:", error);
      await addToSyncQueue(task, selectedProjectId);
    }
  };

  const pruneProjectQueue = async (mutateQueue) => {
    if (!selectedProjectId) return;

    await mutateQueueByKey(
      getSyncQueueKey(ownerUserId, selectedProjectId),
      mutateQueue,
      getLegacySyncQueueKey(selectedProjectId)
    );
  };

  const makePaftaPasifSafe = async (paftaId) => {
    if (!window.confirm("Paftayı pasif yapmak istediğinize emin misiniz?")) return;
    await setPaftaActiveState(paftaId, false);
  };

  const makePaftaAktifSafe = async (paftaId) => {
    if (!window.confirm("Paftayı aktif yapmak istediğinize emin misiniz?")) return;
    await setPaftaActiveState(paftaId, true);
  };

  const deletePaftaSafe = async (paftaId) => {
    const pafta = paftalar.find((item) => item.id === paftaId);
    const paftaName = String(pafta?.name || paftaId || "Pafta");
    const confirmed = window.confirm(
      `"${paftaName}" paftasını kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
    );
    if (!confirmed) return;

    const typedPhrase = window.prompt(
      'Silme işlemini onaylamak için "paftayı sil" yazın:',
      ""
    );
    if (typedPhrase == null) return;

    const isValidPaftaDeletePhrase =
      normalizeDeleteConfirmationText(typedPhrase) ===
      normalizeDeleteConfirmationText("paftayı sil");
    if (!isValidPaftaDeletePhrase) {
      alert('Doğrulama metni hatalı. Silme işlemi iptal edildi.');
      return;
    }

    const baseUpdatedAt = pafta?.updatedAt ?? null;
    const paftaPointIds = new Set(
      points.filter((point) => point.paftaId === paftaId).map((point) => point.id)
    );
    const paftaTeamIds = new Set(
      teams.filter((team) => team.paftaId === paftaId).map((team) => team.id)
    );
    const removeLocalPafta = () => {
      setPaftalar((previous) => previous.filter((item) => item.id !== paftaId));
      setPoints((previous) => previous.filter((point) => point.paftaId !== paftaId));
      setTeams((previous) => previous.filter((team) => team.paftaId !== paftaId));
    };
    const pruneDeletedPaftaTasks = () =>
      pruneProjectQueue((queue) =>
        queue.filter((item) => {
          if (item.type === "deletePafta" && item.paftaId === paftaId) return false;
          if (item.type === "updatePafta" && item.paftaId === paftaId) return false;
          if (item.type === "createPoint" && item.data?.paftaId === paftaId) return false;
          if (item.type === "updatePoint" && paftaPointIds.has(item.pointId)) return false;
          if (item.type === "createTeam" && item.data?.paftaId === paftaId) return false;
          if (item.type === "updateTeam" && paftaTeamIds.has(item.teamId)) return false;
          if (item.type === "deleteTeam" && paftaTeamIds.has(item.teamId)) return false;
          return true;
        })
      );

    const task = {
      type: "deletePafta",
      projectId: selectedProjectId,
      paftaId,
      baseUpdatedAt,
    };

    if (!isOnline) {
      await pruneDeletedPaftaTasks();
      removeLocalPafta();
      await addToSyncQueue(task, selectedProjectId);
      return;
    }

    try {
      await apiDeletePafta(paftaId, baseUpdatedAt);
      removeLocalPafta();
    } catch (error) {
      if (isConflictError(error)) {
        await reloadWorkspaceFromServer();
        alert(getApiErrorMessage(error, "Pafta başka bir oturumda güncellendi."));
        return;
      }

      if (isAuthzError(error)) {
        await reloadWorkspaceFromServer();
        alert(getApiErrorMessage(error, "Pafta silme yetkiniz bulunmuyor."));
        return;
      }

      console.error("Pafta silinemedi:", error);
      await pruneDeletedPaftaTasks();
      removeLocalPafta();
      await addToSyncQueue(task, selectedProjectId);
    }
  };

  const deleteTeamSafe = async (teamId) => {
    const team = teams.find((item) => item.id === teamId);
    const teamName = team?.name || "Takım";
    const baseUpdatedAt = team?.updatedAt ?? null;
    const pruneDeletedTeamTasks = () =>
      pruneProjectQueue((queue) =>
        queue
          .filter((item) => {
            if (item.type === "deleteTeam" && item.teamId === teamId) return false;
            if (item.type === "updateTeam" && item.teamId === teamId) return false;
            if (item.type === "createTeam" && item.data?.id === teamId) return false;
            return true;
          })
          .map((item) => {
            if (item.type === "createPoint" && item.data?.ekipId === teamId) {
              return {
                ...item,
                revision: Number(item.revision || 1) + 1,
                updatedAt: Date.now(),
                data: {
                  ...item.data,
                  ekipId: null,
                },
              };
            }

            if (item.type === "updatePoint" && item.updates?.ekipId === teamId) {
              return {
                ...item,
                revision: Number(item.revision || 1) + 1,
                updatedAt: Date.now(),
                updates: {
                  ...item.updates,
                  ekipId: null,
                },
              };
            }

            return item;
          })
      );
    const removeLocalTeam = () => {
      setTeams((previous) => previous.filter((item) => item.id !== teamId));
      setPoints((previous) =>
        previous.map((point) =>
          point.ekipId === teamId ? { ...point, ekipId: null } : point
        )
      );
    };

    const deleteTask = {
      type: "deleteTeam",
      projectId: selectedProjectId,
      teamId,
      baseUpdatedAt,
    };
    const logTask = {
      taskId: createClientEntityId("log"),
      type: "createLog",
      projectId: selectedProjectId,
      data: {
        type: "team_deleted",
        message: `Takım silindi: ${teamName}`,
      },
    };

    if (!isOnline) {
      await pruneDeletedTeamTasks();
      removeLocalTeam();
      await addToSyncQueue(deleteTask, selectedProjectId);
      await addToSyncQueue(logTask, selectedProjectId);
      return;
    }

    try {
      await apiDeleteTeam(teamId, baseUpdatedAt);
      removeLocalTeam();
      await createProjectLog(selectedProjectId, {
        ...logTask.data,
        clientRequestId: logTask.taskId,
      });
    } catch (error) {
      if (isConflictError(error)) {
        await reloadWorkspaceFromServer();
        alert(getApiErrorMessage(error, "Takım başka bir oturumda güncellendi."));
        return;
      }

      if (isAuthzError(error)) {
        await reloadWorkspaceFromServer();
        alert(getApiErrorMessage(error, "Takım silme yetkiniz bulunmuyor."));
        return;
      }

      console.error("Takım silinemedi:", error);
      await pruneDeletedTeamTasks();
      removeLocalTeam();
      await addToSyncQueue(deleteTask, selectedProjectId);
      await addToSyncQueue(logTask, selectedProjectId);
    }
  };

  const assignTeamToPointSafe = async (paftaId, pointId, ekipId) => {
    const finalEkipId =
      ekipId === "Atanmamis" || ekipId === "Hepsi" ? null : ekipId;
    const point = points.find((item) => item.id === pointId);
    const baseUpdatedAt = point?.updatedAt ?? null;

    setPoints((previous) =>
      previous.map((item) =>
        item.id === pointId ? { ...item, ekipId: finalEkipId } : item
      )
    );

    const task = {
      type: "updatePoint",
      projectId: selectedProjectId,
      pointId,
      baseUpdatedAt,
      updates: { ekipId: finalEkipId },
    };

    if (!isOnline) {
      await addToSyncQueue(task, selectedProjectId);
      return;
    }

    try {
      const updatedPoint = await apiUpdatePoint(pointId, {
        ekipId: finalEkipId,
        baseUpdatedAt,
      });
      if (updatedPoint?.id) {
        applyPointResponseFields(pointId, updatedPoint, ["ekipId"]);
      } else {
        setPoints((previous) =>
          previous.map((item) =>
            item.id === pointId
              ? {
                ...item,
                ekipId: finalEkipId,
              }
              : item
          )
        );
      }
    } catch (error) {
      if (isConflictError(error)) {
        const current = getConflictCurrent(error);
        if (current?.updatedAt != null) {
          try {
            const retriedPoint = await apiUpdatePoint(pointId, {
              ekipId: finalEkipId,
              baseUpdatedAt: current.updatedAt,
            });
            if (retriedPoint?.id) {
              applyPointResponseFields(pointId, retriedPoint, ["ekipId"]);
            } else {
              setPoints((previous) =>
                previous.map((item) =>
                  item.id === pointId
                    ? {
                      ...item,
                      ekipId: finalEkipId,
                    }
                    : item
                )
              );
            }
            return;
          } catch (retryError) {
            const latestCurrent = isConflictError(retryError)
              ? getConflictCurrent(retryError)
              : null;
            await addToSyncQueue(
              {
                ...task,
                baseUpdatedAt:
                  latestCurrent?.updatedAt ?? current.updatedAt ?? baseUpdatedAt ?? null,
              },
              selectedProjectId
            );
            alert(
              getApiErrorMessage(
                retryError,
                "Nokta başka bir oturumda güncellendi. Takım ataması kuyruğa alındı."
              )
            );
            return;
          }
        }

        await reloadWorkspaceFromServer();
        alert(getApiErrorMessage(error, "Nokta başka bir oturumda güncellendi."));
        return;
      }

      console.error("Noktaya ekip atanamadı:", error);
      await addToSyncQueue(task, selectedProjectId);
    }
  };

  const persistTeamMembersSafe = async (teamId, members, logPayload = null) => {
    const team = teams.find((item) => item.id === teamId);
    const baseUpdatedAt = team?.updatedAt ?? null;
    const task = {
      type: "updateTeam",
      projectId: selectedProjectId,
      teamId,
      baseUpdatedAt,
      data: { members },
    };
    const logTask = logPayload
      ? {
          taskId: createClientEntityId("log"),
          type: "createLog",
          projectId: selectedProjectId,
          data: logPayload,
        }
      : null;

    if (!isOnline) {
      await addToSyncQueue(task, selectedProjectId);
      if (logTask) {
        await addToSyncQueue(logTask, selectedProjectId);
      }
      return;
    }

    try {
      const updatedTeam = await apiUpdateTeam(teamId, {
        members,
        baseUpdatedAt,
      });
      replaceTeamRow(updatedTeam);
      if (logTask) {
        await createProjectLog(selectedProjectId, {
          ...logTask.data,
          clientRequestId: logTask.taskId,
        });
      }
    } catch (error) {
      if (isConflictError(error)) {
        const current = getConflictCurrent(error);
        if (current) {
          replaceTeamRow(current);
        } else {
          await reloadWorkspaceFromServer();
        }
        alert(getApiErrorMessage(error, "Takım başka bir oturumda güncellendi."));
        return;
      }

      if (isAuthzError(error)) {
        await reloadWorkspaceFromServer();
        alert(getApiErrorMessage(error, "Takım düzenleme yetkiniz bulunmuyor."));
        return;
      }

      console.error("Takım üyeleri güncellenemedi:", error);
      await addToSyncQueue(task, selectedProjectId);
      if (logTask) {
        await addToSyncQueue(logTask, selectedProjectId);
      }
    }
  };

  const handleAddMemberSafe = async (teamId, memberName) => {
    const team = teams.find((item) => item.id === teamId);
    if (!team) return;

    const members = [...(team.members || []), memberName];
    updateTeamMembersLocal(teamId, members);

    await persistTeamMembersSafe(teamId, members, {
      type: "team_member_added",
      message: `${memberName} kullanıcısı ${team.name} takımına eklendi.`,
    });
  };

  const handleEditMemberSafe = async (teamId, index, newName) => {
    const team = teams.find((item) => item.id === teamId);
    if (!team) return;

    const members = [...(team.members || [])];
    members[index] = newName;
    updateTeamMembersLocal(teamId, members);
    await persistTeamMembersSafe(teamId, members);
  };

  const handleDeleteMemberSafe = async (teamId, index) => {
    const team = teams.find((item) => item.id === teamId);
    if (!team) return;

    const deletedMemberName = team.members[index];
    const members = team.members.filter((_, memberIndex) => memberIndex !== index);
    updateTeamMembersLocal(teamId, members);

    await persistTeamMembersSafe(teamId, members, {
      type: "team_member_removed",
      message: `${deletedMemberName} kullanıcısı ${team.name} takımından çıkarıldı.`,
    });
  };

  const addTemplateRow = () => {
    const nextTemplate = [
      ...sortedTemplate,
      {
        id: generateTemplateRowId("yeni_alan"),
        label: "Yeni Alan",
        visible: true,
        bind: null,
        type: "text",
      },
    ];
    persistTemplate(nextTemplate);
  };

  const resetTemplateToDefault = () => {
    if (
      window.confirm(
        "Tüm veri girişi alanları varsayılana dönecek. Emin misiniz?"
      )
    ) {
      persistTemplate([...DEFAULT_TEMPLATE]);
    }
  };

  const renameTemplateRow = (rowId, providedLabel = null) => {
    const row = sortedTemplate.find((item) => item.id === rowId);
    if (!row) return;

    const nextLabel =
      providedLabel ??
      window.prompt("Yeni alan adı:", row.label || "");

    if (!nextLabel || !String(nextLabel).trim()) return;

    const nextTemplate = sortedTemplate.map((item) =>
      item.id === rowId ? { ...item, label: String(nextLabel).trim() } : item
    );
    persistTemplate(nextTemplate);
  };

  const deleteTemplateRow = (rowId) => {
    const nextTemplate = sortedTemplate.filter((item) => item.id !== rowId);
    persistTemplate(nextTemplate);
  };

  const hideTemplateRow = (rowId) => {
    const nextTemplate = sortedTemplate.map((item) =>
      item.id === rowId ? { ...item, visible: item.visible === false ? true : false } : item
    );
    persistTemplate(nextTemplate);
  };

  const moveTemplateRow = (rowId, direction) => {
    const currentIndex = sortedTemplate.findIndex((item) => item.id === rowId);
    if (currentIndex === -1) return;

    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= sortedTemplate.length) return;

    const nextTemplate = [...sortedTemplate];
    [nextTemplate[currentIndex], nextTemplate[targetIndex]] = [
      nextTemplate[targetIndex],
      nextTemplate[currentIndex],
    ];

    persistTemplate(nextTemplate);
  };

  return {
    projects:
      String(projectsOwnerUserId || "") === String(ownerUserId || "")
        ? projects
        : [],
    setProjects,
    paftalar:
      String(hydratedOwnerUserId || "") === String(ownerUserId || "") &&
      String(hydratedProjectId || "") === String(selectedProjectId || "")
        ? paftalar
        : [],
    setPaftalar,
    points:
      String(hydratedOwnerUserId || "") === String(ownerUserId || "") &&
      String(hydratedProjectId || "") === String(selectedProjectId || "")
        ? points
        : [],
    setPoints,
    teams:
      String(hydratedOwnerUserId || "") === String(ownerUserId || "") &&
      String(hydratedProjectId || "") === String(selectedProjectId || "")
        ? teams
        : [],
    setTeams,
    measurementTemplate:
      String(hydratedOwnerUserId || "") === String(ownerUserId || "") &&
      String(hydratedProjectId || "") === String(selectedProjectId || "")
        ? measurementTemplate
        : [],
    setMeasurementTemplate,
    hydratedProjectId,
    createEmptyProject: createEmptyProjectSafe,
    renameProject: renameProjectSafe,
    deleteProject: deleteProjectSafe,
    changePointStatus: changePointStatusSafe,
    paftaGorunurlukDegistir,
    makePaftaPasif: makePaftaPasifSafe,
    makePaftaAktif: makePaftaAktifSafe,
    deletePafta: deletePaftaSafe,
    addNewTeam,
    deleteTeam: deleteTeamSafe,
    assignTeamToPoint: assignTeamToPointSafe,
    createPointLocalFirst: createPointLocalFirstSafe,
    moveTemplateRow,
    addTemplateRow,
    renameTemplateRow,
    hideTemplateRow,
    deleteTemplateRow,
    resetTemplateToDefault,
    handleAddMember: handleAddMemberSafe,
    handleEditMember: handleEditMemberSafe,
    handleDeleteMember: handleDeleteMemberSafe,
    reloadWorkspaceFromServer,
  };
}
